import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/lib/upload';
import { RadioChangeEvent, TablePaginationConfig } from 'antd';
import { useInterval, useUpdateEffect } from 'usehooks-ts';
import { useSetRecoilState } from 'recoil';
import cloneDeep from 'lodash/cloneDeep';

import './index.less';

import SourceParameters from '../SourceParameters';
import SourcePreviewing from '../SourcePreviewing';
import SourceBuilding from '../SourceBuilding';
import { areFilesEqual, FileTableStore, IFileTableItem } from '../FileTable';
import { IResourceTableItem } from '../ResourceTable';
import IbButton from '../../../../../components/common/IbButton';
import IbTooltip from '../../../../../components/common/IbTooltip';
import IbModal, { IbModalPosition } from '../../../../../components/common/IbModal';
import {
  FilePurpose,
  FileSourceParametersSettings,
  IngestionProcessCreationRequest,
  IngestionResourceAction,
  IngestionResourceModel,
  IngestionSignalCommand,
  IngestionSignalCreationRequest,
  IngestionSignalSubject,
  IngestionSourceCommand,
  IngestionSourceCreationRequest,
  IngestionSourceModelOutput,
  IngestionSourceParametersOutput,
  IngestionSourceParametersType,
  IngestionSourceUpdatingRequest,
} from '../../../../../../gamma-api';
import { isCancelableStage, isSourceBuilding, isSourcePreviewing } from '../../utils';
import IbIcon from '../../../../../components/common/IbIcon';
import { LARGE_PAGE_SIZE, RESOURCES_PAGE_SIZE } from '../../../../../constants';
import { defaultApi } from '../../../../../apis';
import { alertsSelectorAdd } from '../../../../../recoil';
import IbTypography from '../../../../../components/common/IbTypography';
import IbRadioGroup from '../../../../../components/common/IbRadioGroup';
import IbRadioButton from '../../../../../components/common/IbRadioButton';
import IbSpin from '../../../../../components/common/IbSpin';
import { FormAPI } from '../../../../../types';
import { getSourceTypeIconName } from '../../../../../utils/ingestionSourceUtil';
import { IFilterableDebounceInputValue } from '../../../../../components/common/FilterableDebounceInput';

import { createParametersForType } from './utils';
import {
  MAIN_CLASS_NAME,
  RADIO_BUTTON_CLASS_NAME,
  RADIO_BUTTON_LABEL_CLASS_NAME,
  RADIO_GROUP_CLASS_NAME,
  TITLE_CLASS_NAME,
} from './const';

export enum SourceModalStep {
  SourceTypeSelection = 'SourceTypeSelection',
  Parameters = 'Parameters',
  Previewing = 'Previewing',
  Building = 'Building',
}

export enum SourceModalParametersStep {
  Main = 'Main',
  Extra = 'Extra',
}

const REFRESH_INTERVAL = 3000; // 3 seconds.
const DEFAULT_MODAL_WIDTH = '75%';
const SOURCE_TYPE_SELECTION_STEP_MODAL_WIDTH = 424;

export const getCurrentModalStep = (source?: IngestionSourceModelOutput): SourceModalStep => {
  if (!source) return SourceModalStep.Parameters;

  if (isSourceBuilding(source)) {
    return SourceModalStep.Building;
  }

  if (isSourcePreviewing(source)) {
    return SourceModalStep.Previewing;
  }

  return SourceModalStep.Parameters;
};

export interface ISourceModalProps {
  visible: boolean;
  projectId: string;
  sourceId?: string;
  sourceType?: IngestionSourceParametersType;
  onChange?: (sourceId: string) => void;
  onClose?: () => void;
}

const SourceModal: React.FC<ISourceModalProps> = ({ visible, projectId, sourceId, sourceType, onChange, onClose }) => {
  const { t } = useTranslation();
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const formRef = useRef<FormAPI>(null);
  const lastRequestedSourceIdRef = useRef<string | null>(null);

  const [step, setStep] = useState<SourceModalStep>(SourceModalStep.Parameters);
  const [parametersStep, setParametersStep] = useState(SourceModalParametersStep.Main);
  const [cancelling, setCancelling] = useState(false);
  const [source, setSource] = useState<IngestionSourceModelOutput>();

  const [itemLoading, setItemLoading] = useState(false);
  const [fileList, setFileList] = useState<IFileTableItem[]>([]);
  const [resourceList, setResourceList] = useState<IngestionResourceModel[]>([]);
  const [resourceFilter, setResourceFilter] = useState<IFilterableDebounceInputValue>();
  const [resourcePagination, setResourcePagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: RESOURCES_PAGE_SIZE,
    total: 0,
  });
  const [pageLoading, setPageLoading] = useState(false);

  const currentSourceId = sourceId ?? source?.source_id;

  const [selectedCreationType, setSelectedCreationType] = useState<IngestionSourceParametersType>(
    IngestionSourceParametersType.Website
  );

  const parameters = source?.versions.draft.source.parameters;

  const [currentParameters, setCurrentParameters] = useState<IngestionSourceParametersOutput | undefined>(
    parameters ? cloneDeep(parameters) : undefined
  );

  const rolling = itemLoading || !!source?.versions.rolling || fileList.some((file) => file.processing);
  const cancellable = !itemLoading && !!source?.versions.rolling && !cancelling && isCancelableStage(source.stage);

  const fetchSourceItem = async (sourceId: string) => {
    try {
      lastRequestedSourceIdRef.current = sourceId;
      const response = await defaultApi.getOneSourceIngestionSourcesSourceIdGet(sourceId);

      if (lastRequestedSourceIdRef.current === sourceId) {
        setSource(response.data);
        return response.data;
      }
    } catch (err) {
      addAlert({ content: t('Failed to load source'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchResourceList = async (sourceId: string) => {
    const actions: IngestionResourceAction[] = [];
    if (resourceFilter?.filter?.withRemain) {
      actions.push(IngestionResourceAction.Remain);
    }
    if (resourceFilter?.filter?.withCreate) {
      actions.push(IngestionResourceAction.Create);
    }
    if (resourceFilter?.filter?.withUpdate) {
      actions.push(IngestionResourceAction.Update);
    }
    if (resourceFilter?.filter?.withDelete) {
      actions.push(IngestionResourceAction.Delete);
    }

    try {
      const resourcesResponse = await defaultApi.searchResourcesIngestionResourcesGet(
        null,
        [sourceId],
        null,
        actions,
        resourceFilter?.searchText,
        (resourcePagination.current || 1) - 1,
        RESOURCES_PAGE_SIZE,
        true
      );
      setResourceList(resourcesResponse.data.items || []);
      setResourcePagination((prev) => ({ ...prev, total: resourcesResponse.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: t('Failed to load resources'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchServerFiles = async (parameters?: IngestionSourceParametersOutput) => {
    if (parameters?.type !== IngestionSourceParametersType.File) {
      setFileList([]);
      return;
    }

    if (!currentSourceId) {
      setFileList([]);
      return;
    }

    const settings = parameters.settings as FileSourceParametersSettings;
    try {
      const response = await defaultApi.searchFiles({
        filtration: {
          file_ids: settings.files?.map((file) => file.file_id),
          source_ids: [currentSourceId],
        },
        pagination: {
          page_size: LARGE_PAGE_SIZE,
        },
      });

      setFileList(
        (response.data.items || []).map((item) => ({
          client: undefined,
          server: item,
          processing: false,
        }))
      );
    } catch (err) {
      addAlert({ content: t('Failed to load files'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const getActionTooltip = () => {
    if (itemLoading) {
      return t('Source modal: Updating: Tooltip: Page is loading');
    }
    if (source?.versions.rolling) {
      return t('Source modal: Updating: Tooltip: Source is processing');
    }
    if (fileList.some((file) => file.processing)) {
      return t('Source modal: Updating: Tooltip: Files are processing');
    }
    return t('Source modal: Updating: Tooltip: Action is not available');
  };

  const getCancellationTooltip = () => {
    if (itemLoading) {
      return t('Source modal: Updating: Tooltip: Page is loading');
    }
    if (!source?.versions.rolling) {
      return t('Source modal: Updating: Tooltip: Source is not processing');
    }
    if (cancelling) {
      return t('Source modal: Updating: Tooltip: Process is cancelling');
    }
    if (!isCancelableStage(source.stage)) {
      return t('Source modal: Updating: Tooltip: Stage is not cancelable');
    }
    return t('Source modal: Updating: Tooltip: Cancellation is not available');
  };

  const onResourceFilterChange = (value: IFilterableDebounceInputValue) => {
    setResourceFilter(value);
    setResourcePagination({ ...resourcePagination, current: 1 });
  };

  const onResourcePaginationChange = (page: number) => setResourcePagination({ ...resourcePagination, current: page });

  useEffect(() => {
    setCancelling(false);
  }, [source?.versions.rolling?.process_id]);

  useUpdateEffect(() => {
    if (resourcePagination.current !== 1) {
      setResourcePagination({ ...resourcePagination, current: 1 });
    }
  }, [step]);

  const onResourcePageChange = async () => {
    if (!source) {
      return;
    }

    setPageLoading(true);
    await fetchResourceList(source.source_id);
    setPageLoading(false);
  };

  useEffect(() => {
    onResourcePageChange().finally();
  }, [
    resourceFilter?.searchText,
    resourceFilter?.filter?.withRemain,
    resourceFilter?.filter?.withCreate,
    resourceFilter?.filter?.withUpdate,
    resourceFilter?.filter?.withDelete,
    resourcePagination.current,
    resourcePagination.pageSize,
  ]);

  const resourceItems = new Map<string, IResourceTableItem>();

  for (const resource of resourceList) {
    const url = resource.url;

    let resourceItem = resourceItems.get(url);
    if (resourceItem) {
      resourceItem.resource = resource;
      continue;
    }

    resourceItem = {
      resource: resource,
      file: undefined,
    };
    resourceItems.set(url, resourceItem);
  }

  for (const file of fileList) {
    if (!file.server) {
      continue;
    }

    const url = file.server.urls.public;
    let resourceItem = resourceItems.get(url);
    if (resourceItem) {
      resourceItem.file = file;
      continue;
    }

    resourceItem = {
      resource: undefined,
      file,
    };
    resourceItems.set(url, resourceItem);
  }

  const initSource = async () => {
    setCancelling(false);

    if (!sourceId) {
      setStep(SourceModalStep.SourceTypeSelection);
      setCurrentParameters(createParametersForType(selectedCreationType));
      return;
    }

    setItemLoading(true);

    const sourceItem = await fetchSourceItem(sourceId);
    const parameters = cloneDeep(sourceItem?.versions.draft.source.parameters);

    await fetchServerFiles(parameters);
    setCurrentParameters(parameters);
    setStep(getCurrentModalStep(sourceItem));
    setParametersStep(SourceModalParametersStep.Main);
    formRef.current?.reset();

    setItemLoading(false);

    setPageLoading(true);
    await fetchResourceList(sourceId);
    setPageLoading(false);
  };

  const initModal = async () => {
    if (!visible) {
      return;
    }

    await initSource();
  };

  useEffect(() => {
    void initModal();
  }, [sourceId, visible]);

  const onRefreshLoadAsync = async () => {
    if (source) {
      await fetchSourceItem(source.source_id);
      await fetchResourceList(source.source_id);
    }
  };

  const onRefreshLoadSync = () => {
    onRefreshLoadAsync().finally();
  };

  useInterval(onRefreshLoadSync, REFRESH_INTERVAL);

  useEffect(() => {
    if (currentParameters?.type === IngestionSourceParametersType.File) {
      setCurrentParameters({
        ...currentParameters,
        settings: {
          ...currentParameters.settings,
          source_type: IngestionSourceParametersType.File,
          files: fileList
            .filter((file) => file.server)
            .map((file) => ({ file_id: file.server?.file_id || '', metadata: {} })),
        },
      });
    }
  }, [fileList]);

  const createSource = async (parameters: IngestionSourceParametersOutput) => {
    const request: IngestionSourceCreationRequest = {
      project_id: projectId,
      parameters: parameters,
    };

    let createdSourceId: string;
    try {
      const response = await defaultApi.createSourceIngestionSourcesPost(request);
      createdSourceId = response.data.source_id;
    } catch (err) {
      addAlert({ content: t('Failed to create source'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }

    if (parameters.type === IngestionSourceParametersType.File) {
      const files = [...fileList];

      for (const file of files) {
        if (!file.server) {
          continue;
        }

        try {
          await defaultApi.updateFileParametersGeneralFilesFileIdParametersPut(file.server.file_id, {
            source_id: createdSourceId,
            external_id: file.server.external_id,
            purposes: [FilePurpose.Index],
            project_id: file.server.project_id,
            content_format: file.server.content.format,
            content_store: file.server.content.store,
          });
        } catch (err) {
          addAlert({ content: t('Failed to create file'), type: 'error' });
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    }

    await fetchSourceItem(createdSourceId);
    onChange?.(createdSourceId);

    return createdSourceId;
  };

  const tryUpdateSource = async (sourceId: string, parameters: IngestionSourceParametersOutput): Promise<boolean> => {
    const sourceRequest: IngestionSourceUpdatingRequest = {
      source_id: sourceId,
      parameters: parameters,
    };

    try {
      await defaultApi.updateSourceIngestionSourcesSourceIdPut(sourceId, sourceRequest);
      return true;
    } catch (err) {
      addAlert({ content: t('Failed to update source'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return false;
    }
  };

  const createOrUpdateSource = async (sourceId: string | undefined, parameters: IngestionSourceParametersOutput) => {
    let newSourceId = undefined;
    if (!sourceId) {
      newSourceId = await createSource(parameters);
    } else {
      const result = await tryUpdateSource(sourceId, parameters);
      if (!result) {
        return;
      }
    }

    return sourceId || newSourceId;
  };

  const previewSource = async (parameters: IngestionSourceParametersOutput) => {
    setItemLoading(true);

    const updatedSourceId = await createOrUpdateSource(currentSourceId, parameters);

    if (!updatedSourceId) return;

    const processRequest: IngestionProcessCreationRequest = {
      project_id: projectId,
      brief_instructions: [
        {
          source_id: updatedSourceId,
          command: IngestionSourceCommand.Preview,
          action: null,
          stages: {},
        },
      ],
    };

    try {
      await defaultApi.createProcessIngestionProcessesPost(processRequest);
    } catch (err) {
      addAlert({ content: t('Failed to start source previewing'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }

    await fetchSourceItem(updatedSourceId);
    await fetchResourceList(updatedSourceId);

    onChange?.(updatedSourceId);
  };

  const buildSource = async (parameters: IngestionSourceParametersOutput) => {
    const updatedSourceId = await createOrUpdateSource(currentSourceId, parameters);

    if (!updatedSourceId) return;

    const processRequest: IngestionProcessCreationRequest = {
      project_id: projectId,
      brief_instructions: [
        {
          source_id: updatedSourceId,
          command: IngestionSourceCommand.Build,
          action: null,
          stages: {},
        },
      ],
    };

    try {
      await defaultApi.createProcessIngestionProcessesPost(processRequest);
    } catch (err) {
      addAlert({ content: t('Failed to start source building'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }

    await fetchSourceItem(updatedSourceId);
    await fetchResourceList(updatedSourceId);

    onChange?.(updatedSourceId);
  };

  const cancelProcess = async () => {
    if (!currentSourceId) {
      return;
    }

    const processId = source?.versions.rolling?.process_id;
    if (!processId) {
      return;
    }

    const signalRequest: IngestionSignalCreationRequest = {
      process_id: processId,
      subject: IngestionSignalSubject.Process,
      command: IngestionSignalCommand.Cancel,
      metadata: {},
    };

    try {
      await defaultApi.createSignalIngestionSignalsPost(signalRequest);
    } catch (err) {
      addAlert({ content: t('Failed to cancel source ingestion'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }
  };

  const restartProcess = async () => {
    if (!currentSourceId) {
      return;
    }

    const processRequest: IngestionProcessCreationRequest = {
      project_id: projectId,
      brief_instructions: [
        {
          source_id: currentSourceId,
          command: IngestionSourceCommand.Restart,
          action: null,
          stages: {},
        },
      ],
    };

    try {
      await defaultApi.createProcessIngestionProcessesPost(processRequest);
    } catch (err) {
      addAlert({ content: t('Failed to restart source ingestion'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }

    if (source) {
      await fetchSourceItem(currentSourceId);
      await fetchResourceList(currentSourceId);
    }
    onChange?.(currentSourceId);
  };

  const onFileCreate = async (files: RcFile[], store: FileTableStore): Promise<void> => {
    if (store === FileTableStore.Client) {
      const newItems = [...fileList, ...files.map((file) => ({ client: file, server: undefined, processing: false }))];
      setFileList(newItems);
    }

    let oldItems = [...fileList];
    let newItems = [...fileList, ...files.map((file) => ({ client: file, server: undefined, processing: true }))];

    setFileList(newItems);

    for (const file of files) {
      try {
        const response = await defaultApi.createFileFormGeneralFilesFormPost(
          file.uid,
          [FilePurpose.Index],
          file,
          projectId,
          currentSourceId,
          undefined,
          undefined
        );
        const uploadedFile = { client: file, server: response.data.model, processing: false };
        oldItems = [...oldItems, uploadedFile];
        newItems = newItems.map((item) => (areFilesEqual(item, uploadedFile) ? uploadedFile : item));
        setFileList(newItems);
      } catch (err) {
        addAlert({ content: t('Failed to create file'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setFileList(oldItems);
        return;
      }
    }
  };

  const onFileDelete = async (files: IFileTableItem[], store: FileTableStore): Promise<void> => {
    if (store === FileTableStore.Client) {
      const newItems = fileList.filter((item) => files.some((file) => !areFilesEqual(file, item)));
      setFileList(newItems);
      return;
    }

    let oldItems = [...fileList];
    let newItems = fileList.map((item) =>
      files.some((file) => areFilesEqual(file, item)) ? { ...item, processing: true } : item
    );

    setFileList(newItems);

    for (const file of files) {
      try {
        if (file.server) {
          await defaultApi.deleteOneFileGeneralFilesFileIdDelete(file.server.file_id);
        }
        oldItems = oldItems.filter((item) => !areFilesEqual(item, file));
        newItems = newItems.filter((item) => !areFilesEqual(item, file));
        setFileList(newItems);
      } catch (err) {
        addAlert({ content: t('Failed to delete file'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setFileList(oldItems);
        return;
      }
    }
  };

  const onStartClick = async () => {
    if (rolling || !currentParameters) {
      return;
    }

    setItemLoading(true);

    setStep(SourceModalStep.Building);
    await buildSource(currentParameters);

    setItemLoading(false);
  };

  const onCancelClick = async () => {
    if (!cancellable) {
      return;
    }

    setCancelling(true);

    await cancelProcess();
  };

  const onBuildingRestart = async () => {
    if (rolling) {
      return;
    }

    setItemLoading(true);

    await restartProcess();

    setItemLoading(false);
  };

  const onModalCancel = () => {
    onClose?.();
  };

  const renderTitle = () => {
    if (!sourceId && step === SourceModalStep.SourceTypeSelection) {
      return t('Source modal: Creation: Title');
    }

    const currentSourceType = sourceType ?? currentParameters?.type ?? selectedCreationType;
    const titleText =
      parametersStep === SourceModalParametersStep.Extra && currentSourceType === IngestionSourceParametersType.Elma365
        ? t(`Source type: ELMA365 application parameters`)
        : t(`Source type: ${currentSourceType}`);

    return (
      <div className={TITLE_CLASS_NAME}>
        <IbIcon iconName={getSourceTypeIconName(currentSourceType)} size={20} />
        <span>{titleText}</span>
        {itemLoading && <IbSpin />}
      </div>
    );
  };

  const renderTypeSelectionContent = () => {
    const onRadioChange = (e: RadioChangeEvent) => {
      setSelectedCreationType(e.target.value);
      setCurrentParameters(createParametersForType(e.target.value));
    };

    return (
      <>
        <IbTypography.Paragraph type="secondary">{t('Source modal: Creation: Description')}</IbTypography.Paragraph>
        <IbRadioGroup
          buttonStyle="outline"
          className={RADIO_GROUP_CLASS_NAME}
          optionType="default"
          value={selectedCreationType}
          onChange={onRadioChange}
        >
          <IbRadioButton
            key={IngestionSourceParametersType.Website}
            showCircle
            className={RADIO_BUTTON_CLASS_NAME}
            value={IngestionSourceParametersType.Website}
          >
            <div className={RADIO_BUTTON_LABEL_CLASS_NAME}>
              <IbIcon iconName="international" size={20} />
              <span>{t(`Source type: ${IngestionSourceParametersType.Website}`)}</span>
            </div>
          </IbRadioButton>
          <IbRadioButton
            key={IngestionSourceParametersType.File}
            showCircle
            className={RADIO_BUTTON_CLASS_NAME}
            value={IngestionSourceParametersType.File}
          >
            <div className={RADIO_BUTTON_LABEL_CLASS_NAME}>
              <IbIcon iconName="paperclip" size={20} />
              <span>{t(`Source type: ${IngestionSourceParametersType.File}`)}</span>
            </div>
          </IbRadioButton>
          <IbRadioButton
            key={IngestionSourceParametersType.Elma365}
            showCircle
            className={RADIO_BUTTON_CLASS_NAME}
            value={IngestionSourceParametersType.Elma365}
          >
            <div className={RADIO_BUTTON_LABEL_CLASS_NAME}>
              <IbIcon iconName="elma365" size={20} />
              <span>{t(`Source type: ${IngestionSourceParametersType.Elma365}`)}</span>
            </div>
          </IbRadioButton>
          <IbRadioButton
            key={IngestionSourceParametersType.Confluence}
            showCircle
            className={RADIO_BUTTON_CLASS_NAME}
            value={IngestionSourceParametersType.Confluence}
          >
            <div className={RADIO_BUTTON_LABEL_CLASS_NAME}>
              <IbIcon iconName="confluence" size={20} />
              <span>{t(`Source type: ${IngestionSourceParametersType.Confluence}`)}</span>
            </div>
          </IbRadioButton>
        </IbRadioGroup>
      </>
    );
  };

  const renderTypeSelectionButtons = () => {
    const onNextClick = () => {
      setStep(SourceModalStep.Parameters);
      setParametersStep(SourceModalParametersStep.Main);
    };

    const onCancelClick = () => onClose?.();

    return (
      <>
        <IbButton onClick={onNextClick}>{t('Source modal: Creation: Next')}</IbButton>
        <IbButton type="secondary" onClick={onCancelClick}>
          {t('Source modal: Creation: Cancel')}
        </IbButton>
      </>
    );
  };

  const renderParametersContent = () => {
    if (!currentParameters && !sourceType && !selectedCreationType) {
      return null;
    }

    const renderParameters = currentParameters ?? createParametersForType(sourceType ?? selectedCreationType);

    const onParametersChange = (newParameters: IngestionSourceParametersOutput) => {
      setCurrentParameters(newParameters);
    };

    return (
      <SourceParameters
        files={fileList}
        formRef={formRef}
        parameters={renderParameters}
        sourceLoading={itemLoading}
        step={parametersStep}
        onChange={onParametersChange}
        onFileCreate={onFileCreate}
        onFileDelete={onFileDelete}
      />
    );
  };

  const renderParametersButtons = () => {
    const disabled = rolling;

    const onNextClick = async () => {
      if (disabled || !currentParameters) {
        return;
      }

      if (!formRef.current?.validate()) {
        return;
      }

      setItemLoading(true);

      if (currentParameters.type === IngestionSourceParametersType.File) {
        setStep(SourceModalStep.Building);
        await buildSource(currentParameters);
      } else if (currentParameters.type === IngestionSourceParametersType.Elma365) {
        setParametersStep(SourceModalParametersStep.Extra);
      } else {
        setStep(SourceModalStep.Previewing);
        await previewSource(currentParameters);
      }

      setItemLoading(false);
    };

    const onBackClick = () => {
      setStep(SourceModalStep.SourceTypeSelection);
    };

    const getNextButtonTitle = (sourceType?: string) => {
      switch (sourceType) {
        case IngestionSourceParametersType.File:
          return t('Source modal: Updating: Start');
        default:
          return t('Source modal: Updating: Next');
      }
    };

    const renderNextButton = () => {
      const button = (
        <IbButton disabled={disabled} onClick={onNextClick}>
          {getNextButtonTitle(currentParameters?.type)}
        </IbButton>
      );

      if (!disabled) {
        return button;
      }

      return <IbTooltip title={getActionTooltip()}>{button}</IbTooltip>;
    };

    const renderBackButton = () => {
      return (
        <IbButton type="secondary" onClick={onBackClick}>
          {t('Source modal: Updating: Back')}
        </IbButton>
      );
    };

    return (
      <>
        {renderNextButton()}
        {!source && renderBackButton()}
      </>
    );
  };

  const renderExtraParametersButtons = () => {
    const onNextClick = async () => {
      if (rolling || !currentParameters) {
        return;
      }

      setItemLoading(true);

      // NOTE: extra parameters are only used with ELMA365 source type.
      setStep(SourceModalStep.Previewing);
      await previewSource?.(currentParameters);

      setItemLoading(false);
    };

    const onBackClick = () => {
      setParametersStep(SourceModalParametersStep.Main);
    };

    const renderNextButton = () => {
      const button = (
        <IbButton disabled={rolling} onClick={onNextClick}>
          {t('Source modal: Updating: Next')}
        </IbButton>
      );

      if (!rolling) {
        return button;
      }

      return <IbTooltip title={getActionTooltip()}>{button}</IbTooltip>;
    };

    const renderBackButton = () => {
      return (
        <IbButton type="secondary" onClick={onBackClick}>
          {t('Source modal: Updating: Back')}
        </IbButton>
      );
    };

    return (
      <>
        {renderNextButton()}
        {renderBackButton()}
      </>
    );
  };

  const renderPreviewingContent = () => {
    if (!source) {
      return null;
    }

    return (
      <SourcePreviewing
        pageLoading={pageLoading}
        resourceFilter={resourceFilter}
        resourcePagination={resourcePagination}
        resources={Array.from(resourceItems.values())}
        source={source}
        sourceLoading={itemLoading}
        onResourceFilterChange={onResourceFilterChange}
        onResourcePaginationChange={onResourcePaginationChange}
      />
    );
  };

  const renderPreviewingButtons = () => {
    const onBackClick = () => {
      if (rolling) {
        return;
      }

      setStep(SourceModalStep.Parameters);
      setParametersStep(SourceModalParametersStep.Extra);
    };

    const renderStartButton = () => {
      const button = (
        <IbButton disabled={rolling} onClick={onStartClick}>
          {t('Source modal: Updating: Start')}
        </IbButton>
      );

      if (!rolling) {
        return button;
      }

      return <IbTooltip title={getActionTooltip()}>{button}</IbTooltip>;
    };

    const renderBackButton = () => {
      const button = (
        <IbButton disabled={rolling} type="secondary" onClick={onBackClick}>
          {t('Source modal: Updating: Back')}
        </IbButton>
      );

      if (!rolling) {
        return button;
      }

      return <IbTooltip title={getActionTooltip()}>{button}</IbTooltip>;
    };

    const renderCancelButton = () => {
      const button = (
        <IbButton disabled={!cancellable} status="error" type="secondary" onClick={onCancelClick}>
          {t('Source modal: Updating: Cancel')}
        </IbButton>
      );

      if (cancellable) {
        return button;
      }

      return <IbTooltip title={getCancellationTooltip()}>{button}</IbTooltip>;
    };

    return (
      <>
        {renderStartButton()}
        {renderBackButton()}
        {renderCancelButton()}
      </>
    );
  };

  const renderBuildingContent = () => {
    if (!source) {
      return null;
    }

    return (
      <SourceBuilding
        pageLoading={pageLoading}
        resourcePagination={resourcePagination}
        resources={Array.from(resourceItems.values())}
        source={source}
        sourceLoading={itemLoading}
        onResourcePaginationChange={onResourcePaginationChange}
        onRestart={onBuildingRestart}
      />
    );
  };

  const renderBuildingButtons = () => {
    const onCloseClick = () => {
      onClose?.();
    };

    const renderCloseButton = () => {
      return <IbButton onClick={onCloseClick}>{t('Source modal: Updating: Close')}</IbButton>;
    };

    const renderCancelButton = () => {
      const button = (
        <IbButton disabled={!cancellable} status="error" type="secondary" onClick={onCancelClick}>
          {t('Source modal: Updating: Cancel')}
        </IbButton>
      );

      if (cancellable) {
        return button;
      }

      return <IbTooltip title={getCancellationTooltip()}>{button}</IbTooltip>;
    };

    return (
      <>
        {renderCloseButton()}
        {renderCancelButton()}
      </>
    );
  };

  const renderContent = () => {
    if (step == SourceModalStep.SourceTypeSelection) {
      return renderTypeSelectionContent();
    }

    if (step === SourceModalStep.Parameters || step === undefined) {
      return renderParametersContent();
    }

    if (step === SourceModalStep.Previewing) {
      return renderPreviewingContent();
    }

    if (step === SourceModalStep.Building) {
      return renderBuildingContent();
    }

    return null;
  };

  const renderButtons = () => {
    if (step == SourceModalStep.SourceTypeSelection) {
      return renderTypeSelectionButtons();
    }

    if (step === SourceModalStep.Parameters) {
      if (parametersStep === SourceModalParametersStep.Main) {
        return renderParametersButtons();
      }
      if (parametersStep === SourceModalParametersStep.Extra) {
        return renderExtraParametersButtons();
      }
    }

    if (step === SourceModalStep.Previewing) {
      return renderPreviewingButtons();
    }

    if (step === SourceModalStep.Building) {
      return renderBuildingButtons();
    }

    return null;
  };

  const modalWidth =
    step === SourceModalStep.SourceTypeSelection ? SOURCE_TYPE_SELECTION_STEP_MODAL_WIDTH : DEFAULT_MODAL_WIDTH;

  const modalPosition =
    step === SourceModalStep.SourceTypeSelection
      ? IbModalPosition.Centered
      : step === SourceModalStep.Parameters || step === undefined
      ? undefined // default for IbModal
      : IbModalPosition.FullHeight;

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderButtons()}
      position={modalPosition}
      title={renderTitle()}
      visible={visible}
      width={modalWidth}
      onCancel={onModalCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default SourceModal;
